/**
 * CRUD EXTRA-REDUCERS BUILDER — applica il pattern standard di reducers
 * per un entity slice che usa `CrudThunks`. Il chiamante (lo `*Slice.ts`
 * dell'entita') invoca l'helper dentro il proprio `extraReducers` e,
 * se serve, aggiunge altri case custom dopo (es. `analyzeMessage` in
 * MessageSlice, thunks di sub-resource che rinfrescano il parent, ecc.).
 *
 * Lo shape minimo dello state atteso e' `BaseEntityState<T> + totalCount`,
 * che tutte le entita' core gia' rispettano via `BaseEntityState`
 * + l'estensione con `totalCount: number`. Il vincolo `S extends ...`
 * rende il helper utilizzabile anche con state arricchiti (es. TaskState
 * ha `activeTab`/`filters`, MessageState ha `selectedMessageId`/`aiAnalysis`).
 *
 * `byId: Record<number, T>` (parte di BaseEntityState) e' una entity-cache
 * stabile parallela a `items[]`: `items` rappresenta la pagina UI corrente
 * (sostituita ogni fetchAll), `byId` cumula tutte le entity viste, e
 * sopravvive ai re-fetch della lista. I consumer che leggono UN entity
 * by-id senza vincoli di pagina/filtro (es. useEntityActivities, drawer
 * di edit) devono leggere da `byId` per non perdere reattivita' ai
 * mutation downstream.
 *
 * Cases generati:
 *   fetchAll.pending    -> status = 'loading', error = null
 *   fetchAll.fulfilled  -> items, totalCount, upsert byId
 *   fetchAll.rejected   -> status = 'failed', error = payload
 *   fetchOne.fulfilled  -> currentItem + sincronizza items[] se presente + upsert byId
 *   create.fulfilled    -> items.push + upsert byId + totalCount++
 *   update.fulfilled    -> items[idx] = payload + currentItem se match + upsert byId
 *   update.rejected     -> error = payload (items/currentItem invariati)
 *   remove.fulfilled    -> items.filter(!= payload) + currentItem=null se match + delete byId + totalCount--
 *
 * `totalCount` e' mantenuto in sync anche sulle mutation (non solo dalla
 * fetch): senza, dopo create/delete il `rowCount` della DataGrid resta
 * stale e condizioni "lista vuota" basate su totalCount non scattano.
 */

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { BaseEntityState } from '@crm/shared';
import type { CrudThunks } from './createCrudThunks';

/**
 * State atteso: `BaseEntityState<T>` + `totalCount` (dovunque paginato
 * server-side). Gli slice con campi aggiuntivi (TaskState.filters,
 * MessageState.aiAnalysis, ecc.) soddisfano il constraint.
 */
type CrudSliceState<T> = BaseEntityState<T> & { totalCount: number };

export function addCrudCases<T, S extends CrudSliceState<T>>(
  builder: ActionReducerMapBuilder<S>,
  // any deliberato: wrapper generico polimorfo. Forzare tipi propagherebbe
  // 3 generic in più al chiamante, rompendo l'inferenza dei sotto-thunk.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  thunks: CrudThunks<T, any, any, any>,
  getId: (item: T) => number,
): void {
  builder
    .addCase(thunks.fetchAll.pending, (state) => {
      state.status = 'loading';
      state.error = null;
    })
    .addCase(thunks.fetchAll.fulfilled, (state, action) => {
      state.status = 'succeeded';
      // Cast necessario: Immer draft di S non preserva che S.items e'
      // Array<T> dopo l'assign. La sostanza runtime e' corretta.
      (state as CrudSliceState<T>).items = action.payload.items;
      state.totalCount = action.payload.totalCount;
      // Upsert byId: ogni entity vista entra nella cache stabile.
      // Non ripuliamo entries esistenti — pagine successive aggiungono.
      const byId = (state as CrudSliceState<T>).byId;
      for (const item of action.payload.items) byId[getId(item)] = item;
    })
    .addCase(thunks.fetchAll.rejected, (state, action) => {
      state.status = 'failed';
      state.error = (action.payload as string) ?? null;
    })
    .addCase(thunks.fetchOne.fulfilled, (state, action) => {
      (state as CrudSliceState<T>).currentItem = action.payload;
      const id = getId(action.payload);
      // Sincronizza items[]: utile quando i thunk delle sub-resource
      // rinfrescano il parent via fetchOne ma la list mostrerebbe
      // dati stale.
      const items = (state as CrudSliceState<T>).items;
      const idx = items.findIndex((i) => getId(i) === id);
      if (idx !== -1) items[idx] = action.payload;
      // Upsert byId — sempre, anche quando l'item non e' in items[].
      // Questo e' il path principale per popolare la cache da view che
      // fetchano singoli record (es. detail page, hook che derivano
      // entity collegate via relationship).
      (state as CrudSliceState<T>).byId[id] = action.payload;
    })
    .addCase(thunks.create.fulfilled, (state, action) => {
      (state as CrudSliceState<T>).items.push(action.payload);
      (state as CrudSliceState<T>).byId[getId(action.payload)] = action.payload;
      state.totalCount += 1;
    })
    .addCase(thunks.update.fulfilled, (state, action) => {
      const id = getId(action.payload);
      const items = (state as CrudSliceState<T>).items;
      const idx = items.findIndex((i) => getId(i) === id);
      if (idx !== -1) items[idx] = action.payload;
      const cur = (state as CrudSliceState<T>).currentItem;
      if (cur && getId(cur) === id) (state as CrudSliceState<T>).currentItem = action.payload;
      // Upsert byId: garantisce che chi legge da byId veda l'update
      // anche se l'entity non era in items[] (es. hook che derivano da byId).
      (state as CrudSliceState<T>).byId[id] = action.payload;
    })
    .addCase(thunks.update.rejected, (state, action) => {
      // Non modifichiamo items/currentItem: il chiamante intercetta
      // tramite .unwrap() e mostra l'errore nell'UI.
      state.error = (action.payload as string) ?? null;
    })
    .addCase(thunks.remove.fulfilled, (state, action) => {
      const id = action.payload;
      (state as CrudSliceState<T>).items = (state as CrudSliceState<T>).items.filter(
        (i) => getId(i) !== id,
      );
      const cur = (state as CrudSliceState<T>).currentItem;
      if (cur && getId(cur) === id) (state as CrudSliceState<T>).currentItem = null;
      delete (state as CrudSliceState<T>).byId[id];
      state.totalCount = Math.max(0, state.totalCount - 1);
    });
}
